import { LoadingButton } from '@mui/lab';
import { Alert, Button, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreatePartnerFormValues,
  getCreatePartnerDefaultValues,
  mapCreatePartnerResponseErrors,
  toCreatePartnerPayload,
  useCreatePartnerForm,
  validateCreatePartnerForm,
} from 'src/components/CreatePartnerModal/use-create-partner-form';
import { GenericDialog } from 'src/components/GenericDialog/generic-dialog';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { useCreatePartnerWithRelatedData } from 'src/graphql/entities/partner/partner';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';

export const CreatePartnerModal = () => {
  const { closeModal } = useModalMethods(Modals.CreatePartnerModal);
  const { isOpened, modalProps } = useModalState(Modals.CreatePartnerModal);
  const { createPartnerWithRelatedData, loading } = useCreatePartnerWithRelatedData();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { confirm } = useConfirmationModal();

  const formMethods = useForm<CreatePartnerFormValues>({
    defaultValues: getCreatePartnerDefaultValues(),
    mode: 'onChange',
  });

  const {
    clearErrors,
    formState,
    handleSubmit,
    reset,
    setError,
  } = formMethods;

  const createPartnerFormFields = useCreatePartnerForm({
    formMethods,
    isSubmitting: loading,
  });

  const returnToSelector = useCallback(() => {
    reset(getCreatePartnerDefaultValues());
    closeModal();
    modalProps?.onReturnToSelector?.();
  }, [closeModal, modalProps, reset]);

  const handleCloseRequest = useCallback(async () => {
    if (loading) {
      return;
    }

    if (!formState.isDirty) {
      returnToSelector();
      return;
    }

    const shouldDiscard = await confirm({
      title: 'Discard changes',
      body: 'You have unsaved changes. Do you want to discard them?',
      variant: 'delete',
    });

    if (shouldDiscard) {
      returnToSelector();
    }
  }, [confirm, formState.isDirty, loading, returnToSelector]);

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();
    setSubmitError(null);

    const isValid = validateCreatePartnerForm(values, setError);
    if (!isValid) {
      return;
    }

    const response = await createPartnerWithRelatedData(toCreatePartnerPayload(values));

    if (response.errors.length > 0 || !response.partner) {
      mapCreatePartnerResponseErrors(response.errors, setError, setSubmitError);
      return;
    }

    returnToSelector();
  });

  const actionButtons = useMemo(
    () => (
      <Stack direction="row" spacing={1}>
        <Button
          data-testid="create-partner-cancel"
          disabled={loading}
          onClick={() => void handleCloseRequest()}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          data-testid="create-partner-submit"
          disabled={loading}
          loading={loading}
          onClick={onSubmit}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Create
        </LoadingButton>
      </Stack>
    ),
    [handleCloseRequest, loading, onSubmit],
  );

  return (
    <GenericDialog
      modal={Modals.CreatePartnerModal}
      open={isOpened}
      title="Create partner"
      actionButtons={actionButtons}
      onClose={() => void handleCloseRequest()}
      maxWidth="sm"
    >
        <GenericForm<CreatePartnerFormValues> formMethods={formMethods} gap={2.5} pt={2}>
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          {createPartnerFormFields}
        </GenericForm>
    </GenericDialog>
  );
};
